import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Avatar, Tooltip, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Slider,
  Chip, Alert, Grid, IconButton, Button
} from '@mui/material';
import { AddCircle, Delete, Edit, Visibility, Print, Palette, Image, InsertPhoto, People, Settings, Badge } from '@mui/icons-material';
import { SketchPicker } from 'react-color'; // Color picker
import ModernLayout from '../common/ModernLayout';
import ModernCard, { StatsCard } from '../common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from '../common/ModernButton';
import axiosInstance from '../axiosConfig';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PrintableCard from './PrintableCard';
import { useLanguage } from '../../contexts/LanguageContext';

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const Employees = () => {
  const { translate } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openCustomizeModal, setOpenCustomizeModal] = useState(false); // Customization modal
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cardRef = useRef();

  // Customization options
  const [logo, setLogo] = useState(null); // Logo file
  const [backgroundColor, setBackgroundColor] = useState('#ffffff'); // Background color
  const [backgroundImage, setBackgroundImage] = useState(null); // Background image file
  const [fontColor, setFontColor] = useState('#000000'); // Font color
  const [logoSize, setLogoSize] = useState(100); // Logo size in pixels
  const [photoSize, setPhotoSize] = useState(100); // Photo size in pixels
  const [qrSize, setQrSize] = useState(128); // QR size in pixels

  const initialEmployeeState = {
    full_name: '',
    email: '',
    phone: '',
    position: '',
    schedule: '',
    work_days: [],
    photo: null,
    rfid_code: '',
  };

  const [newEmployee, setNewEmployee] = useState(initialEmployeeState);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const projectId = localStorage.getItem('projectId');
      if (!projectId) return console.error('⚠️ No hay projectId guardado');
      const response = await axiosInstance.get(`/api/projects/${projectId}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('❌ Error obteniendo empleados:', error);
      setError('Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCustomization = async () => {
      const projectId = localStorage.getItem('projectId');
      if (!projectId) return;
  
      try {
        const response = await axiosInstance.get(`/api/projects/${projectId}/badge-customizations`);
        const customization = response.data;
  
        if (customization) {
          setLogo(customization.logo_url);
          setBackgroundColor(customization.background_color);
          setBackgroundImage(customization.background_image_url);
          setFontColor(customization.font_color);
          setLogoSize(customization.logo_size);
          setPhotoSize(customization.photo_size);
          setQrSize(customization.qr_size);
        }
      } catch (error) {
        console.error('Error fetching badge customization:', error);
      }
    };
  
    fetchCustomization();
  }, []);

  const saveCustomization = async () => {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) return;
  
    const formData = new FormData();
    if (logo) formData.append('logo', logo);
    if (backgroundImage) formData.append('background_image', backgroundImage);
    formData.append('background_color', backgroundColor);
    formData.append('font_color', fontColor);
    formData.append('logo_size', logoSize);
    formData.append('photo_size', photoSize);
    formData.append('qr_size', qrSize);
  
    try {
      const response = await axiosInstance.post(
        `/api/projects/${projectId}/badge-customizations`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
  
      alert(translate('employments.customizationSaved'));
    } catch (error) {
      console.error('Error saving badge customization:', error);
      alert(translate('employments.customizationError'));
    }
  };

  const handleOpenModal = (employee) => {
    console.log('Selected Employee:', employee); // Debugging

    // Ensure work_days is an array
    const employeeWithParsedWorkDays = {
      ...employee,
      work_days: Array.isArray(employee.work_days) ? employee.work_days : JSON.parse(employee.work_days || '[]'),
    };

    setSelectedEmployee(employeeWithParsedWorkDays);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEmployee(null);
  };

  const handleOpenAddModal = (employee = null) => {
    if (employee) {
      setNewEmployee({ ...employee, work_days: employee.work_days || [] });
      setEditMode(true);
    } else {
      setNewEmployee(initialEmployeeState);
      setEditMode(false);
    }
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    setNewEmployee(initialEmployeeState);
    setError('');
  };

  const handleSaveEmployee = async () => {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) return;

    const formData = new FormData();
    formData.append('full_name', newEmployee.full_name);
    formData.append('email', newEmployee.email);
    formData.append('phone', newEmployee.phone);
    formData.append('position', newEmployee.position);
    formData.append('schedule', newEmployee.schedule);
    formData.append('work_days', JSON.stringify(newEmployee.work_days));
    formData.append('rfid_code', newEmployee.rfid_code);
    if (newEmployee.photo) {
      formData.append('photo', newEmployee.photo);
    }

    // Log FormData entries
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    try {
      setLoading(true);
      if (editMode) {
        await axiosInstance.put(`/api/projects/${projectId}/employees/${newEmployee.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await axiosInstance.post(`/api/projects/${projectId}/employees/register`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      fetchEmployees();
      handleCloseAddModal();
    } catch (error) {
      console.error('❌ Error guardando empleado:', error);
      setError('Error al guardar empleado');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    const projectId = localStorage.getItem('projectId');
    if (!projectId) return;

    try {
      setLoading(true);
      await axiosInstance.delete(`/api/projects/${projectId}/employees/${employeeId}`);
      fetchEmployees();
    } catch (error) {
      console.error('❌ Error eliminando empleado:', error);
      setError('Error al eliminar empleado');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!cardRef.current) {
      console.error('Card reference not found');
      return;
    }

    html2canvas(cardRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const imgWidth = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save('employee-card.pdf');
    });
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewEmployee({ ...newEmployee, photo: file });
    }
  };

  const handleWorkDaysChange = (day) => {
    const updatedWorkDays = newEmployee.work_days.includes(day)
      ? newEmployee.work_days.filter(d => d !== day)
      : [...newEmployee.work_days, day];
    setNewEmployee({ ...newEmployee, work_days: updatedWorkDays });
  };

  return (
    <ModernLayout title={translate('employments.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('employments.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('employments.subtitle')}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('employments.totalEmployees')}
            value={employees.length}
            subtitle={translate('employments.registeredEmployees')}
            icon={<People />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('employments.activeEmployees')}
            value={employees.filter(emp => emp.status === 'active').length}
            subtitle={translate('employments.withActiveAccess')}
            icon={<People />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('employments.withCredentials')}
            value={employees.filter(emp => emp.rfid_code).length}
            subtitle={translate('employments.withRfidAssigned')}
            icon={<Badge />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('employments.withoutCredentials')}
            value={employees.filter(emp => !emp.rfid_code).length}
            subtitle={translate('employments.withoutCredentials')}
            icon={<Badge />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Actions Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {translate('employments.employeesList')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('employments.manageEmployees')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <GlassButton
            startIcon={<Palette />}
            onClick={() => setOpenCustomizeModal(true)}
          >
            {translate('employments.customizeBadges')}
          </GlassButton>
          <GradientButton
            startIcon={<AddCircle />}
            onClick={() => handleOpenAddModal()}
          >
            {translate('employments.addEmployee')}
          </GradientButton>
        </Box>
      </Box>

      {/* Employees Table */}
      <ModernCard
        title={translate('employments.employeesList')}
        subtitle={`${translate('employments.showing')} ${employees.length} ${translate('employments.employees')}`}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{translate('employments.employee')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('employments.position')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('employments.phone')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('employments.schedule')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('employments.workDays')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('employments.credentials')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('employments.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={employee.photo_url}
                        sx={{
                          width: 40,
                          height: 40,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        }}
                      >
                        {employee.full_name?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {employee.full_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {employee.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={employee.position}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>{employee.phone}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {employee.schedule}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {employee.work_days?.length || 0} {translate('employments.daysPerWeek')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={employee.rfid_code ? translate('employments.withCredential') : translate('employments.withoutCredential')}
                      size="small"
                      color={employee.rfid_code ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={translate('employments.viewCredential')}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(employee)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={translate('employments.editEmployee')}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenAddModal(employee)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={translate('employments.deleteEmployee')}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEmployee(employee.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </ModernCard>

      {/* Employee Details Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {translate('employments.credential')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <div ref={cardRef}>
              <PrintableCard
                employee={selectedEmployee}
                logo={logo}
                backgroundColor={backgroundColor}
                backgroundImage={backgroundImage}
                fontColor={fontColor}
                logoSize={logoSize}
                photoSize={photoSize}
                qrSize={qrSize}
              />
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={handleCloseModal}>
            {translate('employments.close')}
          </GlassButton>
          <GradientButton
            startIcon={<Print />}
            onClick={handlePrint}
          >
            {translate('employments.print')}
          </GradientButton>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Employee Modal */}
      <Dialog
        open={openAddModal}
        onClose={handleCloseAddModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editMode ? translate('employments.editEmployee') : translate('employments.addNewEmployee')}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('employments.fullName')}
                  value={newEmployee.full_name}
                  onChange={(e) => setNewEmployee({ ...newEmployee, full_name: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('employments.email')}
                  type="email"
                  value={newEmployee.email}
                  onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('employments.phone')}
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee({ ...newEmployee, phone: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('employments.position')}
                  value={newEmployee.position}
                  onChange={(e) => setNewEmployee({ ...newEmployee, position: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('employments.schedule')}
                  value={newEmployee.schedule}
                  onChange={(e) => setNewEmployee({ ...newEmployee, schedule: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={translate('employments.rfidCode')}
                  value={newEmployee.rfid_code}
                  onChange={(e) => setNewEmployee({ ...newEmployee, rfid_code: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {translate('employments.workDays')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {daysOfWeek.map((day, index) => (
                    <Chip
                      key={day}
                      label={translate(`employments.${day.toLowerCase()}`)}
                      variant={newEmployee.work_days.includes(index) ? 'filled' : 'outlined'}
                      color={newEmployee.work_days.includes(index) ? 'primary' : 'default'}
                      onClick={() => handleWorkDaysChange(index)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {translate('employments.uploadPhoto')}
                </Typography>
                <input
                  accept="image/*"
                  type="file"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                  id="photo-upload"
                />
                <label htmlFor="photo-upload">
                  <GlassButton
                    component="span"
                    startIcon={<InsertPhoto />}
                  >
                    {translate('employments.selectPhoto')}
                  </GlassButton>
                </label>
                {newEmployee.photo && (
                  <Typography variant="caption" sx={{ ml: 2 }}>
                    {translate('employments.selectedFile')} {newEmployee.photo.name}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={handleCloseAddModal}>
            {translate('employments.cancel')}
          </GlassButton>
          <GradientButton
            onClick={handleSaveEmployee}
            disabled={loading}
          >
            {loading ? translate('employments.saving') : (editMode ? translate('employments.update') : translate('employments.save'))}
          </GradientButton>
        </DialogActions>
      </Dialog>

      {/* Customization Modal */}
      <Dialog
        open={openCustomizeModal}
        onClose={() => setOpenCustomizeModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {translate('employments.customizeBadges')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ pt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                {translate('employments.logoSize')}
              </Typography>
              <Slider
                value={logoSize}
                onChange={(e, value) => setLogoSize(value)}
                min={50}
                max={200}
                valueLabelDisplay="auto"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                {translate('employments.photoSize')}
              </Typography>
              <Slider
                value={photoSize}
                onChange={(e, value) => setPhotoSize(value)}
                min={50}
                max={200}
                valueLabelDisplay="auto"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {translate('employments.qrSize')}
              </Typography>
              <Slider
                value={qrSize}
                onChange={(e, value) => setQrSize(value)}
                min={64}
                max={256}
                valueLabelDisplay="auto"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setOpenCustomizeModal(false)}>
            {translate('employments.cancel')}
          </GlassButton>
          <GradientButton onClick={saveCustomization}>
            {translate('employments.saveCustomization')}
          </GradientButton>
        </DialogActions>
      </Dialog>
    </ModernLayout>
  );
};

export default Employees;